/*
 * (c) Copyright, 2024, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect, useContext, useRef } from 'react'
import { Formik, yupToFormErrors } from 'formik'
import { Form, Button, Collapse } from 'react-bootstrap'
import { cloneDeep } from 'lodash'
import * as yup from 'yup'
import { useServiceContext } from '../../../../root'
import { RcmTableDataContext } from '../../../../components/rcm-table/RcmTableDataContext'
import { Select } from '../../../../components/Select/Select'
import { FilterTrigger } from '../../../../components/shared/FilterTrigger'
import ActionsMenuBig from '../../../../components/shared/ActionsMenuBig'
import { ErrorMessageStyled } from '../../../../components/shared/ErrorMessage'
import type { StudyDocumentsFilterData } from '../types'

interface TableFilterProps {
  filter: StudyDocumentsFilterData
  changeTableVisibility: (show: boolean) => void
  setMilestoneVisibility: (show: boolean) => void
}

const TableFilter: React.FC<TableFilterProps> = ({ filter, changeTableVisibility, setMilestoneVisibility }) => {
  const context = useServiceContext()
  const tableContext = useContext(RcmTableDataContext)
  const { translate } = context.service.filter
  const Constants = context.constant
  const formRefLocal = useRef<any>({})
  const isInitialMount = useRef(true)

  // Use filter collapsed state from table context
  const isFilterCollapsed = tableContext?.isFilterCollapsed ?? false

  const completionTypes = [
    { enum: 'NOT_STARTED', value: translate('ETMF_COMPLETION_TYPE_MISSING_DOCUMENTS') },
    { enum: 'IN_PROGRESS', value: translate('ETMF_GRAPHS_LABEL_IN_PROGRESS') },
    { enum: 'COMPLETED', value: translate('ETMF_COMPLETION_TYPE_COMPLETED_DOCUMENTS') },
    { enum: 'COMPLETED_RECENTLY', value: translate('ETMF_COMPLETION_TYPE_COMPLETED_RECENTLY_DOCUMENTS') },
    { enum: 'ALL', value: translate('ETMF_COMPLETION_TYPE_ALL') }
  ]

  const defaultFilter = {
    data: {
      PRODUCT: null,
      STUDY: null,
      ENVIRONMENT: null,
      COUNTRY: null,
      SITE: null,
      level: 'ALL',
      completionType: 'NOT_STARTED',
      ZONE: null,
      SECTION: null,
      artifacts: null,
      tagIds: null,
      milestone: null,
      stage: null,
      rule: null
    }
  }

  const LEVEL = ['ALL', 'PRODUCT', 'STUDY', 'COUNTRY', 'SITE']

  // Validation schema matching legacy implementation
  const validationSchema = yup.object().shape({
    filter: yup.object().shape({
      data: yup.object().shape({
        level: yup
          .string()
          .oneOf(LEVEL)
          .default(() => 'ALL'),
        PRODUCT: yup
          .number()
          .min(1, 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
          .required('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'),
        STUDY: yup
          .number()
          .nullable()
          .when('level', {
            is: (level: string) => level === 'COUNTRY' || level === 'SITE',
            then: (schema: any) =>
              schema.min(1, 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR').required('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
          }),
        ENVIRONMENT: yup
          .number()
          .nullable()
          .when('level', {
            is: (level: string) => level === 'COUNTRY' || level === 'SITE',
            then: (schema: any) =>
              schema.min(1, 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR').required('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
          }),
        COUNTRY: yup
          .mixed()
          .nullable()
          .when('level', {
            is: (level: string) => level === 'COUNTRY' || level === 'SITE',
            then: (schema: any) =>
              schema
                .transform((value: any) => (value === 0 ? null : value === 'all' ? 999999999 : value))
                .required('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
          }),
        SITE: yup
          .mixed()
          .nullable()
          .when('level', {
            is: (level: string) => level === 'SITE',
            then: (schema: any) =>
              schema
                .transform((value: any) => (value === 0 ? null : value === 'all' ? 999999999 : value))
                .required('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
          })
      })
    })
  })

  // Helper to transform data to select options format
  const transformToSelectOptions = (arr: any[], valueKey = 'id', labelKey = 'value', preserveOrigVals = false) => {
    if (Array.isArray(arr)) {
      return arr.map((val) =>
        typeof val === 'string'
          ? { value: val, label: val }
          : {
              ...(preserveOrigVals ? val : {}),
              value: val[valueKey],
              label: val[labelKey]
            }
      )
    }
    return []
  }

  const [initialValues, setInitialValues] = useState(() => {
    const initial = cloneDeep(defaultFilter)
    initial.data.completionType = 'NOT_STARTED'
    return initial
  })
  const [studyDocumentsFilterOptions, setStudyDocumentsFilterOptions] = useState<any>({})
  const [filterForm, setFilterForm] = useState<any>({
    level: 'ALL',
    completionType: 'NOT_STARTED'
  })
  const [properties, setProperties] = useState<any>({})
  const [isInjectingFilter, setIsInjectingFilter] = useState(false)
  const [shouldAutoApplyFilter, setShouldAutoApplyFilter] = useState(false)
  const injectedFilterDataRef = useRef<any>(null)

  // Build filter request object
  const buildFilterForReq = (filter: any) => {
    let fsiId = null
    const DTO: any = {}

    DTO.level = filter.model?.level
      ? filter.model.level.charAt(0).toUpperCase() + filter.model.level.slice(1).toLowerCase()
      : 'ALL'

    if (filter.model?.PRODUCT) {
      DTO.product = filter.model.PRODUCT.fsiPropName || filter.model.PRODUCT.fsiName
      fsiId = filter.model.PRODUCT.fsiId
    }
    if (filter.model?.STUDY) {
      DTO.study = filter.model.STUDY.fsiPropName || filter.model.STUDY.fsiName
      fsiId = filter.model.STUDY.fsiId
    }
    if (filter.model?.ENVIRONMENT) {
      DTO.environment = filter.model.ENVIRONMENT.fsiPropName || filter.model.ENVIRONMENT.fsiName
      fsiId = filter.model.ENVIRONMENT.fsiId
    }
    if (filter.model?.COUNTRY && filter.model.COUNTRY.fsiId !== 'all') {
      DTO.country = filter.model.COUNTRY.fsiPropName || filter.model.COUNTRY.fsiName
      fsiId = filter.model.COUNTRY.fsiId
    }
    if (filter.model?.SITE && filter.model.SITE.fsiId !== 'all') {
      DTO.site = filter.model.SITE.fsiPropName || filter.model.SITE.fsiName
      fsiId = filter.model.SITE.fsiId
    }
    if (filter.model?.ZONE) {
      DTO.zone = filter.model.ZONE.fsiPropName || filter.model.ZONE.label
    }
    if (filter.model?.SECTION) {
      DTO.section = filter.model.SECTION.fsiPropName || filter.model.SECTION.label
    }
    if (filter.model?.tagIds && filter.model.tagIds.length) {
      DTO.tagIds = filter.model.tagIds.map((t: any) => t.value)
    }
    if (filter.model?.artifacts && filter.model.artifacts.length) {
      DTO.artifacts = filter.model.artifacts.map((a: any) => a.label)
    }
    if (filter.model?.milestone) {
      DTO.milestone = filter.model.milestone.label
    }
    if (filter.model?.stage) {
      DTO.stage = filter.model.stage.label
    }
    if (filter.model?.rule) {
      DTO.rule = filter.model.rule.label
    }

    DTO.fsiId = fsiId ? fsiId : null
    DTO.type = filter?.completionType ? filter.completionType : 'ALL'
    DTO.userId = context.service.auth.getUserId()

    return DTO
  }

  // Export function
  const exportDocs = () => {
    if (formRefLocal.current) {
      formRefLocal.current.setFieldValue('filter', cloneDeep(initialValues))
      setFilterForm({ ...defaultFilter.data })
    }
    changeTableVisibility(true)
    setMilestoneVisibility(false)

    const filterData = buildFilterForReq({ completionType: 'ALL', model: defaultFilter.data })
    if (filterData) {
      context.api.etmf.exportMissingDocumentsPropertyBased(filterData)
    }
  }

  // Menu configuration for ActionsMenuBig
  const menuStudyDocumentsOptions = {
    menuId: 'page',
    items: [
      {
        value: 'BTN_FILTER_EXPORT',
        action: function () {
          exportDocs()
        }
      }
    ]
  }

  useEffect(() => {
    // Load initial data
    Promise.all([
      context.api.etmf.getLinkedEntities(),
      context.api.etmf.getSelectionsForEtmfReports(Constants.etmf.level.PRODUCT),
      context.api.files.getTagOptions(undefined, true, true)
    ])
      .then((results) => {
        const propertiesData: any = {}
        const data = results[0]

        const productOptions = results[1].map((item: any) => ({
          ...item,
          value: item?.fsiId,
          label: item?.fsiName,
          fsiPropName: item?.fsiPropName || item?.fsiName
        }))

        setStudyDocumentsFilterOptions((prev: any) => ({
          ...prev,
          PRODUCT: productOptions
        }))

        // getTagOptions returns TagOption[] directly
        propertiesData['tag'] = results[2]
        propertiesData['product'] = { id: data?.entityLinkMap?.PRODUCT }
        propertiesData['trial'] = { id: data?.entityLinkMap?.TRIAL }
        propertiesData['environment'] = { id: data?.entityLinkMap?.ENVIRONMENT }
        propertiesData['country'] = { id: data?.entityLinkMap?.COUNTRY }
        propertiesData['site'] = { id: data?.entityLinkMap?.SITE }
        propertiesData['zone'] = { id: data?.entityLinkMap?.ZONE }
        propertiesData['section'] = { id: data?.entityLinkMap?.SECTION }
        propertiesData['level'] = { id: data?.entityLinkMap?.LEVEL }
        propertiesData['artifact'] = { id: data?.entityLinkMap?.ARTIFACT, usedValues: [] }
        propertiesData['milestone'] = { id: data?.entityLinkMap?.MILESTONE, usedValues: [] }
        propertiesData['stage'] = { id: data?.entityLinkMap?.STAGE, usedValues: [] }
        propertiesData['rule'] = { id: data?.entityLinkMap?.RULE, usedValues: [] }

        // Load property values for artifact, milestone, stage, rule
        for (const key of ['artifact', 'milestone', 'stage', 'rule']) {
          if (propertiesData[key]?.id) {
            context.api.etmf.propertyValuesSearch(propertiesData[key].id).then((data: any) => {
              propertiesData[key].usedValues = data
              setProperties((prev: any) => ({
                ...prev,
                [key]: { ...prev[key], usedValues: data }
              }))
            })
          }
        }

        // Load artifact definition options
        if (data?.entityLinkMap?.ARTIFACT) {
          const artifactId =
            typeof data.entityLinkMap.ARTIFACT === 'string'
              ? parseInt(data.entityLinkMap.ARTIFACT, 10)
              : data.entityLinkMap.ARTIFACT
          context.api.metadata
            .getById(artifactId)
            .then((metaData: any) => {
              if (metaData?.definition?.options) {
                setProperties((prev: any) => ({
                  ...prev,
                  artifact: { ...prev.artifact, values: metaData.definition.options }
                }))
              } else {
                console.warn('[TableFilter] No artifact definition options found in metadata')
              }
            })
            .catch((error) => {
              console.error('[TableFilter] Error loading artifact metadata:', error)
            })
        }

        setProperties(propertiesData)
      })
      .catch((error) => {
        console.error('[TableFilter] Error loading initial data:', error)
      })
  }, [])

  // Sync with incoming filter prop (skip on initial mount)
  useEffect(() => {
    // Skip filter injection on initial page load
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Only inject filter when it's explicitly changed (e.g., clicking graph legend)
    if (filter && Object.keys(filter).length) {
      injectFilter({ completionType: filter.completionType, model: filter })
    }
  }, [JSON.stringify(filter)])

  // Auto-apply filter after injection completes
  useEffect(() => {
    if (shouldAutoApplyFilter && !isInjectingFilter) {
      // Add a delay to ensure all state updates from cascade have completed
      const timer = setTimeout(() => {
        // Use the ref data which has all the values from the initial injection
        const dataToApply = injectedFilterDataRef.current || filterForm
        doFilter(false, dataToApply)
        setShouldAutoApplyFilter(false)
        injectedFilterDataRef.current = null
      }, 500)

      return () => clearTimeout(timer)
    }
  }, [shouldAutoApplyFilter, isInjectingFilter])

  const showStudy = (filterFormData = filterForm) => {
    const level = filterFormData.level?.toUpperCase()
    return ['ALL', 'STUDY', 'COUNTRY', 'SITE', 'ENVIRONMENT'].indexOf(level) >= 0
  }

  const showEnvironment = (filterFormData = filterForm) => {
    const level = filterFormData.level?.toUpperCase()
    return ['ALL', 'STUDY', 'COUNTRY', 'SITE', 'ENVIRONMENT'].indexOf(level) >= 0
  }

  const showCountry = (filterFormData = filterForm) => {
    const level = filterFormData.level?.toUpperCase()
    return ['ALL', 'COUNTRY', 'SITE'].indexOf(level) >= 0
  }

  const showSite = (filterFormData = filterForm) => {
    const level = filterFormData.level?.toUpperCase()
    return ['ALL', 'SITE'].indexOf(level) >= 0
  }

  const showZone = (filterFormData = filterForm) => {
    switch (filterFormData?.level) {
      case LEVEL[1]: // 'PRODUCT'
        return false
      case LEVEL[2]: // 'STUDY'
        return showStudy(filterFormData)
      case Constants.etmf.level.ENVIRONMENT:
        return showEnvironment(filterFormData)
      case LEVEL[3]: // 'COUNTRY'
        return showCountry(filterFormData)
      case LEVEL[4]: // 'SITE'
        return showSite(filterFormData)
      default: // 'ALL'
        return true
    }
  }

  const showSection = (filterFormData = filterForm) => {
    return showZone(filterFormData)
  }

  const isStudyEnabled = (filterFormData = filterForm) => {
    return showStudy(filterFormData) && filterFormData?.PRODUCT
  }

  const isEnvironmentEnabled = (filterFormData = filterForm) => {
    return showEnvironment(filterFormData) && filterFormData?.STUDY
  }

  const isCountryEnabled = (filterFormData = filterForm) => {
    return showCountry(filterFormData) && filterFormData?.ENVIRONMENT
  }

  const isSiteEnabled = (filterFormData = filterForm) => {
    return showSite(filterFormData) && filterFormData?.COUNTRY
  }

  const isZoneEnabled = (filterFormData = filterForm) => {
    switch (filterFormData?.level) {
      case LEVEL[2]: // 'STUDY'
        return isStudyEnabled(filterFormData) && !!filterFormData?.STUDY && !!filterFormData?.ENVIRONMENT
      case Constants.etmf.level.ENVIRONMENT:
        return isEnvironmentEnabled(filterFormData) && !!filterFormData?.ENVIRONMENT
      case LEVEL[3]: // 'COUNTRY'
        return (
          isCountryEnabled(filterFormData) &&
          !!filterFormData?.COUNTRY &&
          filterFormData?.COUNTRY?.fsiName !== translate('ETMF_LABEL_SELECT_ALL_COUNTRIES')
        )
      case LEVEL[4]: // 'SITE'
        return (
          isSiteEnabled(filterFormData) &&
          !!filterFormData?.SITE &&
          filterFormData?.SITE?.fsiName !== translate('ETMF_LABEL_SELECT_ALL_SITES')
        )
      default: // 'ALL' or 'PRODUCT'
        return !!filterForm.SITE
    }
  }

  const isSectionEnabled = () => {
    return isZoneEnabled() && filterForm?.ZONE
  }

  const injectFilter = (incomingFilter: any) => {
    // Prevent multiple simultaneous filter injections
    if (isInjectingFilter) {
      return
    }

    setIsInjectingFilter(true)
    const set = formRefLocal?.current?.setFieldValue
    const levelFilter = incomingFilter?.model
    const filterFormData: any = {
      completionType: incomingFilter?.completionType
    }

    if (set) {
      set('filter.data.completionType', incomingFilter?.completionType)
    }

    // Normalize level to uppercase to match LEVEL array values
    if (levelFilter?.level || levelFilter?.type) {
      const normalizedLevel = (levelFilter.level || levelFilter.type).toUpperCase()
      filterFormData.level = normalizedLevel
      if (set) set('filter.data.level', normalizedLevel)
    } else {
      filterFormData.level = 'ALL'
      if (set) set('filter.data.level', 'ALL')
    }

    // Store all filter values from incoming filter
    if (levelFilter?.PRODUCT || levelFilter?.product) {
      filterFormData.PRODUCT = levelFilter.PRODUCT || levelFilter.product
    }
    if (levelFilter?.STUDY || levelFilter?.trial) {
      filterFormData.STUDY = levelFilter.STUDY || levelFilter.trial
    }
    if (levelFilter?.ENVIRONMENT || levelFilter?.environment) {
      filterFormData.ENVIRONMENT = levelFilter.ENVIRONMENT || levelFilter.environment
    }
    if (levelFilter?.COUNTRY || levelFilter?.country) {
      filterFormData.COUNTRY = levelFilter.COUNTRY || levelFilter.country
    }
    if (levelFilter?.SITE || levelFilter?.site) {
      filterFormData.SITE = levelFilter.SITE || levelFilter.site
    }
    if (levelFilter?.ZONE || levelFilter?.zone) {
      filterFormData.ZONE = levelFilter.ZONE || levelFilter.zone
    }
    if (levelFilter?.SECTION || levelFilter?.section) {
      filterFormData.SECTION = levelFilter.SECTION || levelFilter.section
    }
    if (levelFilter?.tagIds || levelFilter?.tag) {
      if (levelFilter?.tagIds) {
        filterFormData.tagIds = levelFilter.tagIds
      } else {
        const tag = properties?.tag?.filter((t: any) => {
          if (levelFilter.tag instanceof Object) {
            return levelFilter.tag?.value === t?.id
          }
          return t.id === parseInt(levelFilter.tag, 10)
        })
        if (tag && tag.length > 0) {
          filterFormData.tagIds = [{ ...tag[0], value: tag[0]?.id, label: tag[0]?.name }]
        }
      }
    }
    if (levelFilter?.artifacts) {
      filterFormData.artifacts = levelFilter.artifacts
    }
    if (levelFilter?.milestone) {
      // Find matching milestone by label and set with value
      const milestoneLabel = levelFilter.milestone.label
      const milestoneIndex = properties?.milestone?.usedValues?.indexOf(milestoneLabel)
      if (milestoneIndex >= 0) {
        filterFormData.milestone = { value: milestoneIndex + 1, label: milestoneLabel }
      }
    }
    if (levelFilter?.stage) {
      // Find matching stage by label and set with value
      const stageLabel = levelFilter.stage.label
      const stageIndex = properties?.stage?.usedValues?.indexOf(stageLabel)
      if (stageIndex >= 0) {
        filterFormData.stage = { value: stageIndex + 1, label: stageLabel }
      }
    }
    if (levelFilter?.rule) {
      // Find matching rule by label and set with value
      const ruleLabel = levelFilter.rule.label
      const ruleIndex = properties?.rule?.usedValues?.indexOf(ruleLabel)
      if (ruleIndex >= 0) {
        filterFormData.rule = { value: ruleIndex + 1, label: ruleLabel }
      }
    }

    setFilterForm(filterFormData)
    injectedFilterDataRef.current = filterFormData
    changeTableVisibility(true)

    // Trigger cascade loading with pre-populated values - this will load options and set form values
    onLevelChange(true, false, filterFormData)
      .then(() => {
        // After cascade completes, set flag to auto-apply filter
        setShouldAutoApplyFilter(true)
        setIsInjectingFilter(false)
      })
      .catch(() => {
        setIsInjectingFilter(false)
      })
  }

  const clearProps = (skipUntilProp: string, filterFormData: any) => {
    const orderedPropsCaps = ['PRODUCT', 'STUDY', 'ENVIRONMENT', 'COUNTRY', 'SITE', 'ZONE', 'SECTION']
    const indexFrom = orderedPropsCaps.indexOf(skipUntilProp)
    const propsToChange = orderedPropsCaps.slice(indexFrom + 1)

    propsToChange.forEach((prop) => {
      filterFormData[prop] = null
      if (formRefLocal.current) {
        formRefLocal.current.setFieldValue(`filter.data.${prop}`, null)
      }
    })
    setFilterForm((prev: any) => ({ ...prev, ...filterFormData }))
  }

  const doFilter = (isNoRedirectToFirstPage?: boolean, filterFormData?: any) => {
    const dataToFilter = filterFormData || cloneDeep(filterForm)
    if (tableContext && tableContext.applyFilter) {
      tableContext.applyFilter(cloneDeep(dataToFilter))
      if (!isNoRedirectToFirstPage && tableContext.refreshTable) {
        tableContext.refreshTable()
      }
    }
  }

  const onLevelChange = (auto?: boolean, preselect?: boolean | null, filterFormData = cloneDeep(filterForm)) => {
    preselect = preselect === null ? true : preselect
    const studyDocumentsFilterOptionsData = cloneDeep(studyDocumentsFilterOptions)

    return new Promise<void>((resolve, reject) => {
      if (filterFormData?.level || filterFormData?.level === 'ALL' || auto) {
        // Check if PRODUCT options are already loaded
        if (studyDocumentsFilterOptionsData?.PRODUCT) {
          // Check if filterFormData already has PRODUCT value (from graph click)
          if (filterFormData.PRODUCT) {
            const matchingProduct = studyDocumentsFilterOptionsData.PRODUCT.find(
              (p: any) => p.fsiId === filterFormData.PRODUCT.fsiId || p.fsiId === filterFormData.PRODUCT.value
            )
            if (matchingProduct) {
              filterFormData.PRODUCT = matchingProduct
              if (formRefLocal.current) {
                formRefLocal.current.setFieldValue('filter.data.PRODUCT', matchingProduct.fsiId)
              }
              setFilterForm((prev: any) => ({ ...prev, PRODUCT: matchingProduct }))
              // Update the ref with the matched product
              if (injectedFilterDataRef.current) {
                injectedFilterDataRef.current.PRODUCT = matchingProduct
              }
            }
          } else if (preselect && studyDocumentsFilterOptionsData?.PRODUCT?.length === 1) {
            // Auto-select if only one product
            filterFormData.PRODUCT = studyDocumentsFilterOptionsData.PRODUCT[0]
            if (formRefLocal.current) {
              formRefLocal.current.setFieldValue(
                'filter.data.PRODUCT',
                studyDocumentsFilterOptionsData.PRODUCT[0]?.fsiId
              )
            }
            setFilterForm((prev: any) => ({ ...prev, PRODUCT: filterFormData.PRODUCT }))
            // Update the ref with the auto-selected product
            if (injectedFilterDataRef.current) {
              injectedFilterDataRef.current.PRODUCT = filterFormData.PRODUCT
            }
          }
          onProductChange(auto, preselect, filterFormData).then(resolve, reject)
        } else {
          // Load PRODUCT options first
          context.api.etmf.getSelectionsForEtmfReports(Constants.etmf.level.PRODUCT).then((data: any) => {
            const productOptions = data.map((item: any) => ({
              ...item,
              value: item?.fsiId,
              label: item?.fsiName,
              fsiPropName: item?.fsiPropName || item?.fsiName
            }))
            studyDocumentsFilterOptionsData.PRODUCT = productOptions
            setStudyDocumentsFilterOptions((prev: any) => ({
              ...prev,
              PRODUCT: productOptions
            }))

            // Check if filterFormData already has PRODUCT value (from graph click)
            if (filterFormData.PRODUCT) {
              const matchingProduct = productOptions.find(
                (p: any) => p.fsiId === filterFormData.PRODUCT.fsiId || p.fsiId === filterFormData.PRODUCT.value
              )
              if (matchingProduct) {
                filterFormData.PRODUCT = matchingProduct
                if (formRefLocal.current) {
                  formRefLocal.current.setFieldValue('filter.data.PRODUCT', matchingProduct.fsiId)
                }
                setFilterForm((prev: any) => ({ ...prev, PRODUCT: matchingProduct }))
                // Update the ref with the matched product
                if (injectedFilterDataRef.current) {
                  injectedFilterDataRef.current.PRODUCT = matchingProduct
                }
              }
            } else if (preselect && productOptions.length === 1) {
              // Auto-select if only one product
              filterFormData.PRODUCT = productOptions[0]
              if (formRefLocal.current) {
                formRefLocal.current.setFieldValue('filter.data.PRODUCT', productOptions[0]?.fsiId)
              }
              setFilterForm((prev: any) => ({ ...prev, PRODUCT: filterFormData.PRODUCT }))
              // Update the ref with the auto-selected product
              if (injectedFilterDataRef.current) {
                injectedFilterDataRef.current.PRODUCT = filterFormData.PRODUCT
              }
            }

            onProductChange(auto, preselect, filterFormData).then(resolve, reject)
          }, reject)
        }
      } else {
        clearProps('PRODUCT', filterFormData)
        resolve()
      }
    })
  }

  const onProductChange = (auto?: boolean, preselect?: boolean | null, filterFormData = cloneDeep(filterForm)) => {
    preselect = preselect === null ? true : preselect

    return new Promise<void>((resolve) => {
      if (isStudyEnabled(filterFormData)) {
        context.api.etmf
          .getSelectionsForEtmfReports(Constants.etmf.level.STUDY, filterFormData.PRODUCT.fsiId)
          .then((data: any) => {
            const studyOptions = data.map((item: any) => ({
              ...item,
              value: item?.fsiId,
              label: item?.fsiName,
              fsiPropName: item?.fsiPropName || item?.fsiName
            }))

            setStudyDocumentsFilterOptions((prev: any) => ({
              ...prev,
              STUDY: studyOptions
            }))

            // Check if filterFormData already has STUDY value (from graph click)
            if (filterFormData.STUDY) {
              // Find matching option and set it
              const matchingStudy = studyOptions.find(
                (s: any) => s.fsiId === filterFormData.STUDY.fsiId || s.fsiId === filterFormData.STUDY.value
              )
              if (matchingStudy) {
                filterFormData.STUDY = matchingStudy
                if (formRefLocal.current) {
                  formRefLocal.current.setFieldValue('filter.data.STUDY', matchingStudy.fsiId)
                }
                setFilterForm((prev: any) => ({ ...prev, STUDY: matchingStudy }))
                // Update the ref with the matched study
                if (injectedFilterDataRef.current) {
                  injectedFilterDataRef.current.STUDY = matchingStudy
                }
                onStudyChange(auto, preselect, filterFormData).then(resolve)
                return
              }
            }

            // Auto-select if only one option and no pre-populated value
            if (preselect && isStudyEnabled(filterFormData) && studyOptions.length === 1) {
              const updatedForm = { ...filterFormData, STUDY: studyOptions[0] }
              if (formRefLocal.current) {
                formRefLocal.current.setFieldValue('filter.data.STUDY', studyOptions[0].fsiId)
              }
              setFilterForm((prev: any) => ({ ...prev, STUDY: studyOptions[0] }))
              // Update the ref with the auto-selected study
              if (injectedFilterDataRef.current) {
                injectedFilterDataRef.current.STUDY = studyOptions[0]
              }
              onStudyChange(auto, preselect, updatedForm).then(resolve)
            } else {
              onStudyChange(auto, preselect, filterFormData).then(resolve)
            }
          })
      } else {
        clearProps('PRODUCT', filterFormData)
        resolve()
      }
    })
  }

  const onStudyChange = (
    auto?: boolean,
    preselect?: boolean | null,
    filterFormData = cloneDeep(filterForm),
    studyDocumentsFilterOptionsData = cloneDeep(studyDocumentsFilterOptions)
  ) => {
    preselect = preselect === null ? true : preselect

    return new Promise<void>((resolve) => {
      if (filterFormData?.STUDY) {
        if (showEnvironment(filterFormData)) {
          context.api.etmf
            .getSelectionsForEtmfReports(Constants.etmf.level.ENVIRONMENT, filterFormData.STUDY.fsiId)
            .then((data: any) => {
              const envOptions = data.map((item: any) => ({
                ...item,
                value: item?.fsiId,
                label: item?.fsiName,
                fsiPropName: item?.fsiPropName || item?.fsiName
              }))

              studyDocumentsFilterOptionsData.ENVIRONMENT = envOptions
              setStudyDocumentsFilterOptions((prev: any) => ({
                ...prev,
                ENVIRONMENT: envOptions
              }))

              // Check if filterFormData already has ENVIRONMENT value (from graph click)
              if (filterFormData.ENVIRONMENT) {
                const matchingEnv = envOptions.find(
                  (e: any) =>
                    e.fsiId === filterFormData.ENVIRONMENT.fsiId || e.fsiId === filterFormData.ENVIRONMENT.value
                )
                if (matchingEnv) {
                  filterFormData.ENVIRONMENT = matchingEnv
                  if (formRefLocal.current) {
                    formRefLocal.current.setFieldValue('filter.data.ENVIRONMENT', matchingEnv.fsiId)
                  }
                  setFilterForm((prev: any) => ({ ...prev, ENVIRONMENT: matchingEnv }))
                  // Update the ref with the matched environment
                  if (injectedFilterDataRef.current) {
                    injectedFilterDataRef.current.ENVIRONMENT = matchingEnv
                  }
                  onEnvironmentChange(auto, preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve)
                  return
                }
              }

              // Auto-select if only one option
              if (envOptions.length === 1) {
                const updatedForm = { ...filterFormData, ENVIRONMENT: envOptions[0] }
                if (formRefLocal.current) {
                  formRefLocal.current.setFieldValue('filter.data.ENVIRONMENT', envOptions[0].fsiId)
                }
                setFilterForm((prev: any) => ({ ...prev, ENVIRONMENT: envOptions[0] }))
                // Update the ref with the auto-selected environment
                if (injectedFilterDataRef.current) {
                  injectedFilterDataRef.current.ENVIRONMENT = envOptions[0]
                }
                onEnvironmentChange(auto, preselect, updatedForm, studyDocumentsFilterOptionsData).then(resolve)
              } else if (envOptions.length > 1 && !filterFormData.ENVIRONMENT) {
                // Default to Production environment if available and no pre-populated value
                const prodEnv = envOptions.find((env: any) => env.fsiName === 'Production')
                if (prodEnv) {
                  const updatedForm = { ...filterFormData, ENVIRONMENT: prodEnv }
                  if (formRefLocal.current) {
                    formRefLocal.current.setFieldValue('filter.data.ENVIRONMENT', prodEnv.fsiId)
                  }
                  setFilterForm((prev: any) => ({ ...prev, ENVIRONMENT: prodEnv }))
                  // Update the ref with the default environment
                  if (injectedFilterDataRef.current) {
                    injectedFilterDataRef.current.ENVIRONMENT = prodEnv
                  }
                  onEnvironmentChange(auto, preselect, updatedForm, studyDocumentsFilterOptionsData).then(resolve)
                } else {
                  onEnvironmentChange(auto, preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve)
                }
              } else {
                onEnvironmentChange(auto, preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve)
              }
            })
        } else {
          getZones(auto, preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve)
        }
      } else {
        clearProps(Constants.etmf.level.ENVIRONMENT, filterFormData)
        resolve()
      }
    })
  }

  const onEnvironmentChange = (
    auto?: boolean,
    preselect?: boolean | null,
    filterFormData = cloneDeep(filterForm),
    studyDocumentsFilterOptionsData = cloneDeep(studyDocumentsFilterOptions)
  ) => {
    preselect = preselect === null ? true : preselect

    return new Promise<void>((resolve) => {
      if (filterFormData?.ENVIRONMENT) {
        if (showCountry(filterFormData)) {
          context.api.etmf
            .getSelectionsForEtmfReports(Constants.etmf.level.COUNTRY, filterFormData.ENVIRONMENT.fsiId)
            .then((data: any) => {
              let countryOptions = data.map((item: any) => ({
                ...item,
                value: item?.fsiId,
                label: item?.fsiName
              }))

              // Add "All Countries" option if COUNTRY level and multiple options
              if (filterFormData?.level === Constants.etmf.level.COUNTRY && data?.length > 1) {
                countryOptions = [
                  {
                    fsiId: 'all',
                    fsiName: translate('ETMF_LABEL_SELECT_ALL_COUNTRIES'),
                    fsiPropName: translate('ETMF_LABEL_SELECT_ALL_COUNTRIES'),
                    value: 'all',
                    label: translate('ETMF_LABEL_SELECT_ALL_COUNTRIES')
                  },
                  ...countryOptions
                ]
              }

              studyDocumentsFilterOptionsData.COUNTRY = countryOptions
              setStudyDocumentsFilterOptions((prev: any) => ({
                ...prev,
                COUNTRY: countryOptions
              }))

              // Check if filterFormData already has COUNTRY value (from graph click)
              if (filterFormData.COUNTRY) {
                const matchingCountry = countryOptions.find(
                  (c: any) => c.fsiId === filterFormData.COUNTRY.fsiId || c.fsiId === filterFormData.COUNTRY.value
                )
                if (matchingCountry) {
                  filterFormData.COUNTRY = matchingCountry
                  if (formRefLocal.current) {
                    formRefLocal.current.setFieldValue('filter.data.COUNTRY', matchingCountry.fsiId)
                  }
                  setFilterForm((prev: any) => ({ ...prev, COUNTRY: matchingCountry }))
                  onCountryChange(auto, preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve)
                  return
                }
              }

              // Auto-select if only one option
              if (preselect && isCountryEnabled(filterFormData) && countryOptions.length === 1) {
                const updatedForm = { ...filterFormData, COUNTRY: countryOptions[0] }
                if (formRefLocal.current) {
                  formRefLocal.current.setFieldValue('filter.data.COUNTRY', countryOptions[0].fsiId)
                }
                setFilterForm((prev: any) => ({ ...prev, COUNTRY: countryOptions[0] }))
                onCountryChange(auto, preselect, updatedForm, studyDocumentsFilterOptionsData).then(resolve)
              } else {
                onCountryChange(auto, preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve)
              }
            })
        } else {
          getZones(auto, preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve)
        }
      } else {
        clearProps(Constants.etmf.level.ENVIRONMENT, filterFormData)
        resolve()
      }
    })
  }

  const onCountryChange = (
    auto?: boolean,
    preselect?: boolean | null,
    filterFormData = cloneDeep(filterForm),
    studyDocumentsFilterOptionsData = cloneDeep(studyDocumentsFilterOptions)
  ) => {
    preselect = preselect === null ? true : preselect

    return new Promise<void>((resolve) => {
      if (filterFormData?.COUNTRY && filterFormData.COUNTRY.fsiId !== 'all') {
        if (showSite(filterFormData)) {
          context.api.etmf
            .getSelectionsForEtmfReports(Constants.etmf.level.SITE, filterFormData.COUNTRY.fsiId)
            .then((data: any) => {
              let siteOptions = data.map((item: any) => ({
                ...item,
                value: item?.fsiId,
                label: item?.fsiName
              }))

              // Add "All Sites" option if SITE level and multiple options
              if (filterFormData?.level === Constants.etmf.level.SITE && data?.length > 1) {
                siteOptions = [
                  {
                    fsiId: 'all',
                    fsiName: translate('ETMF_LABEL_SELECT_ALL_SITES'),
                    fsiPropName: translate('ETMF_LABEL_SELECT_ALL_SITES'),
                    value: 'all',
                    label: translate('ETMF_LABEL_SELECT_ALL_SITES')
                  },
                  ...siteOptions
                ]
              }

              studyDocumentsFilterOptionsData.SITE = siteOptions
              setStudyDocumentsFilterOptions((prev: any) => ({
                ...prev,
                SITE: siteOptions
              }))

              // Check if filterFormData already has SITE value (from graph click)
              if (filterFormData.SITE) {
                const matchingSite = siteOptions.find(
                  (s: any) => s.fsiId === filterFormData.SITE.fsiId || s.fsiId === filterFormData.SITE.value
                )
                if (matchingSite) {
                  filterFormData.SITE = matchingSite
                  if (formRefLocal.current) {
                    formRefLocal.current.setFieldValue('filter.data.SITE', matchingSite.fsiId)
                  }
                  setFilterForm((prev: any) => ({ ...prev, SITE: matchingSite }))
                  onSiteChange(auto, preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve)
                  return
                }
              }

              // Auto-select if only one option
              if (preselect && isSiteEnabled(filterFormData) && siteOptions.length === 1) {
                const updatedForm = { ...filterFormData, SITE: siteOptions[0] }
                if (formRefLocal.current) {
                  formRefLocal.current.setFieldValue('filter.data.SITE', siteOptions[0].fsiId)
                }
                setFilterForm((prev: any) => ({ ...prev, SITE: siteOptions[0] }))
                onSiteChange(auto, preselect, updatedForm, studyDocumentsFilterOptionsData).then(resolve)
              } else {
                onSiteChange(auto, preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve)
              }
            })
        } else {
          getZones(auto, preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve)
        }
      } else {
        getZones(auto, preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve)
      }
    })
  }

  const onSiteChange = (
    auto?: boolean,
    preselect?: boolean | null,
    filterFormData = cloneDeep(filterForm),
    studyDocumentsFilterOptionsData = cloneDeep(studyDocumentsFilterOptions)
  ) => {
    return new Promise<void>((resolve) => {
      // Validate that the selected site still exists in options
      if (
        filterFormData?.SITE?.fsiName &&
        !studyDocumentsFilterOptionsData.SITE?.find((s: any) => s.fsiName === filterFormData.SITE.fsiName)
      ) {
        filterFormData.SITE = null
      }
      getZones(auto, preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve)
    })
  }

  const getZones = (
    auto?: boolean,
    preselect?: boolean | null,
    filterFormData = cloneDeep(filterForm),
    studyDocumentsFilterOptionsData = cloneDeep(studyDocumentsFilterOptions)
  ) => {
    preselect = preselect === null ? true : preselect

    return new Promise<void>((resolve, reject) => {
      let fsiId = null

      if (filterFormData?.PRODUCT) fsiId = filterFormData.PRODUCT.fsiId
      if (filterFormData?.STUDY) fsiId = filterFormData.STUDY.fsiId
      if (filterFormData?.ENVIRONMENT) fsiId = filterFormData.ENVIRONMENT.fsiId
      if (filterFormData?.COUNTRY && filterFormData.COUNTRY.fsiId !== 'all') fsiId = filterFormData.COUNTRY.fsiId
      if (filterFormData?.SITE && filterFormData.SITE.fsiId !== 'all') fsiId = filterFormData.SITE.fsiId

      context.api.etmf.getSelectionsForEtmfReports('ZONE', fsiId).then(
        (data: any) => {
          // Store raw data (not transformed) in studyDocumentsFilterOptionsData
          studyDocumentsFilterOptionsData.ZONE = data

          // Transform for UI display
          const zoneOptions = data.map((item: any) => ({
            ...item,
            value: item?.fsiId,
            label: item?.fsiName
          }))

          setStudyDocumentsFilterOptions((prev: any) => ({
            ...prev,
            ZONE: data // Store raw data in state
          }))

          // Auto-select if only one option
          if (preselect && data.length === 1) {
            filterFormData.ZONE = zoneOptions[0]
            setTimeout(() => {
              formRefLocal.current?.setFieldValue('filter.data.ZONE', zoneOptions[0].fsiId)
            }, 0)
            setFilterForm((prev: any) => ({ ...prev, ZONE: zoneOptions[0] }))
          } else if (!data.find((z: any) => z.fsiId === filterFormData?.ZONE?.fsiId)) {
            // Clear if current selection is no longer valid
            filterFormData.ZONE = null
            setFilterForm((prev: any) => ({ ...prev, ZONE: null }))
          }

          // Continue cascade based on auto flag
          if (auto) {
            getSections(preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve, reject)
          } else {
            onZoneChange(undefined, preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve, reject)
          }
        },
        (error: any) => {
          reject()
          if (error?.data?.message) {
            context.service.toast.error(error.data.message)
          }
        }
      )
    })
  }

  const onZoneChange = (
    auto?: boolean,
    preselect?: boolean | null,
    filterFormData = cloneDeep(filterForm),
    studyDocumentsFilterOptionsData = cloneDeep(studyDocumentsFilterOptions)
  ) => {
    return new Promise<void>((resolve, reject) => {
      if (filterFormData?.ZONE) {
        getSections(preselect, filterFormData, studyDocumentsFilterOptionsData).then(resolve, reject)
      } else {
        clearProps('ZONE', filterFormData)
        resolve()
      }
    })
  }

  const getSections = (
    preselect?: boolean | null,
    filterFormData = cloneDeep(filterForm),
    studyDocumentsFilterOptionsData = cloneDeep(studyDocumentsFilterOptions)
  ) => {
    preselect = preselect === null ? true : preselect

    return new Promise<void>((resolve) => {
      if (filterFormData?.ZONE) {
        context.api.etmf.getSelectionsForEtmfReports('SECTION', filterFormData.ZONE.fsiId).then((data: any) => {
          // Transform data using transformToSelectOptions
          const sectionOptions = transformToSelectOptions(data, 'fsiId', 'fsiName', true)

          studyDocumentsFilterOptionsData.SECTION = sectionOptions
          setStudyDocumentsFilterOptions((prev: any) => ({
            ...prev,
            SECTION: sectionOptions
          }))

          // Auto-select if only one option
          if (preselect && sectionOptions.length === 1) {
            filterFormData.SECTION = sectionOptions[0]
            setTimeout(() => {
              formRefLocal.current?.setFieldValue('filter.data.SECTION', sectionOptions[0].value)
            }, 0)
            setFilterForm((prev: any) => ({ ...prev, SECTION: sectionOptions[0] }))
          } else if (!sectionOptions.find((s: any) => s.value === filterFormData?.SECTION?.value)) {
            // Clear if current selection is no longer valid
            filterFormData.SECTION = null
            setFilterForm((prev: any) => ({ ...prev, SECTION: null }))
          }

          resolve()
        })
      } else {
        resolve()
      }
    })
  }

  return (
    <div id="etmf-study-docs">
      <div className="sub-page-header mt21">
        <div className="d-flex flex-wrap">
          <h2 className="align-left align-top filter-title">{translate('ETMF_TRIAL_DOCUMENTS')}</h2>
          <div className="align-right align-bottom d-flex">
            <ActionsMenuBig {...menuStudyDocumentsOptions} />
            <FilterTrigger noPersist={true} />
          </div>
        </div>
      </div>
      <Collapse in={!isFilterCollapsed}>
        <div className="filter-wrapper">
          <Formik
            validateOnChange
            validateOnMount={false}
            innerRef={(el) => {
              formRefLocal.current = el
            }}
            validate={(values) => {
              return validationSchema
                .validate(values, {
                  abortEarly: false,
                  context: values
                })
                .then(
                  () => {
                    return {}
                  },
                  (err: any) => yupToFormErrors(err)
                )
            }}
            enableReinitialize
            initialValues={{ filter: initialValues }}
            onReset={() => {
              setInitialValues({ ...defaultFilter })
              setFilterForm({ ...defaultFilter?.data })
              changeTableVisibility(true)
            }}
            onSubmit={() => {
              if (filterForm?.completionType === 'ALL') {
                changeTableVisibility(false)
              } else {
                doFilter(false)
              }
            }}
          >
            {({ values, setFieldValue, handleSubmit, handleReset, errors }) => (
              <>
                <div className="row">
                  {/* Column 1: Level and Completion Type */}
                  <div className="col-xs-4">
                    <div className="mb20">
                      <div className="w100p mb0">
                        <Form.Group controlId="b4a3d3f0-5052-45a6-9f7b-1f088e2042fc">
                          <Form.Label className="mb0 fs14">{translate('EMTF_LEVEL')}</Form.Label>
                          {LEVEL.map((item, index) => (
                            <Form.Check
                              key={index}
                              type="radio"
                              name="filter.data.level"
                              className="classic w100p"
                              value={item}
                              checked={values?.filter?.data?.level === item}
                              onChange={() => {
                                const formData = cloneDeep(filterForm)
                                setFieldValue('filter.data.level', item)
                                formData.level = item
                                setFilterForm(formData)
                                onLevelChange(true, null, formData)
                              }}
                              id={`b4a3d3f0-5052-45a6-9f7b-1f088e2042fc-${item}`}
                              label={
                                <div style={{ paddingLeft: '8px', fontWeight: 'normal' }}>
                                  {translate(`EMTF_MD_${item}`)}
                                </div>
                              }
                            />
                          ))}
                        </Form.Group>
                      </div>
                    </div>
                    <Form.Group controlId="77d5ae28-203f-4a10-9ccd-07e64d504bab">
                      <Form.Label className="mb0 fs14">{translate('ETMF_LABEL_DOC_COMPLETION_STATUS')}</Form.Label>
                      {completionTypes.map((item, index) => (
                        <Form.Check
                          key={index}
                          type="radio"
                          name="filter.data.completionType"
                          className="classic w100p"
                          value={item?.enum}
                          checked={values?.filter?.data?.completionType === item?.enum}
                          onChange={(evt: any) => {
                            const formData = cloneDeep(filterForm)
                            setFieldValue('filter.data.completionType', item?.enum)
                            changeTableVisibility(evt?.target?.value !== 'ALL')
                            formData.completionType = item?.enum
                            setFilterForm(formData)
                          }}
                          id={`77d5ae28-203f-4a10-9ccd-07e64d504bab-${item}`}
                          label={<div style={{ paddingLeft: '8px', fontWeight: 'normal' }}>{item?.value}</div>}
                        />
                      ))}
                    </Form.Group>
                  </div>

                  {/* Column 2: Hierarchical Dropdowns */}
                  <div className="col-xs-4">
                    <div className="w100p">
                      <Form.Group controlId="d2a6c136-c9c6-42bc-a0cd-189aea78a840">
                        <Form.Label className="fs14">{translate('EMTF_MD_PRODUCT')}</Form.Label>
                        <Select
                          className="fs14"
                          placeholder={<div className="placeholder-text">{translate('SELECT_PRODUCT')}</div>}
                          name="filter.data.PRODUCT"
                          value={filterForm?.PRODUCT?.value ? filterForm.PRODUCT.value : null}
                          options={studyDocumentsFilterOptions?.PRODUCT || []}
                          onChange={(option: any) => {
                            const filterFormData = cloneDeep(filterForm)
                            if (option === null) {
                              filterFormData.PRODUCT = null
                              setFieldValue('filter.data.PRODUCT', null)
                            } else {
                              filterFormData.PRODUCT = {
                                value: option.value,
                                fsiId: option.value,
                                fsiName: option.label,
                                fsiPropName: option.fsiPropName || option.label
                              }
                              setFieldValue('filter.data.PRODUCT', option.value)
                            }
                            filterFormData.STUDY = null
                            filterFormData.ENVIRONMENT = null
                            filterFormData.COUNTRY = null
                            filterFormData.SITE = null
                            setFilterForm(filterFormData)
                            onProductChange(undefined, true, filterFormData)
                          }}
                          isClearable={true}
                          id="d2a6c136-c9c6-42bc-a0cd-189aea78a840"
                        />
                        <ErrorMessageStyled name="filter.data.PRODUCT" />
                      </Form.Group>
                    </div>
                    {showStudy() && (
                      <div className="w100p">
                        <Form.Group controlId="e2b79597-d02c-4194-810c-ed55c2e563e4">
                          <Form.Label className="fs14">{translate('EMTF_MD_TRIAL')}</Form.Label>
                          <Select
                            className="fs14"
                            placeholder={<div className="placeholder-text">{translate('ETMF_LABEL_SELECT_STUDY')}</div>}
                            name="filter.data.STUDY"
                            value={filterForm?.STUDY?.value ? filterForm.STUDY.value : null}
                            options={studyDocumentsFilterOptions?.STUDY || []}
                            onChange={(option: any) => {
                              const filterFormData = cloneDeep(filterForm)
                              if (option === null) {
                                filterFormData.STUDY = null
                                setFieldValue('filter.data.STUDY', null)
                              } else {
                                filterFormData.STUDY = {
                                  value: option.value,
                                  fsiId: option.value,
                                  fsiName: option.label,
                                  fsiPropName: option.fsiPropName || option.label
                                }
                                setFieldValue('filter.data.STUDY', option.value)
                              }
                              filterFormData.ENVIRONMENT = null
                              filterFormData.COUNTRY = null
                              filterFormData.SITE = null
                              setFilterForm(filterFormData)
                              onStudyChange(undefined, true, filterFormData)
                            }}
                            isClearable={true}
                            id="e2b79597-d02c-4194-810c-ed55c2e563e4"
                            isDisabled={!isStudyEnabled()}
                          />
                          <ErrorMessageStyled name="filter.data.STUDY" />
                        </Form.Group>
                      </div>
                    )}
                    {showEnvironment() && (
                      <div className="w100p">
                        <Form.Group controlId="d075077b-2b92-4a15-bf98-3ae4f6b24699">
                          <Form.Label className="fs14">{translate('EMTF_MD_ENVIRONMENT')}</Form.Label>
                          <Select
                            className="fs14"
                            placeholder={<div className="placeholder-text">{translate('SELECT_ENVIRONMENT')}</div>}
                            name="filter.data.ENVIRONMENT"
                            value={filterForm?.ENVIRONMENT?.value ? filterForm.ENVIRONMENT.value : null}
                            options={studyDocumentsFilterOptions?.ENVIRONMENT || []}
                            onChange={(option: any) => {
                              const filterFormData = cloneDeep(filterForm)
                              if (option === null) {
                                filterFormData.ENVIRONMENT = null
                                setFieldValue('filter.data.ENVIRONMENT', null)
                              } else {
                                filterFormData.ENVIRONMENT = {
                                  value: option.value,
                                  fsiId: option.value,
                                  fsiName: option.label,
                                  fsiPropName: option.fsiPropName || option.label
                                }
                                setFieldValue('filter.data.ENVIRONMENT', option.value)
                              }
                              filterFormData.COUNTRY = null
                              filterFormData.SITE = null
                              setFilterForm(filterFormData)
                              onEnvironmentChange(undefined, true, filterFormData)
                              getZones(undefined, undefined, filterFormData)
                            }}
                            isClearable={true}
                            id="d075077b-2b92-4a15-bf98-3ae4f6b24699"
                            isDisabled={!isEnvironmentEnabled()}
                          />
                          <ErrorMessageStyled name="filter.data.ENVIRONMENT" />
                        </Form.Group>
                      </div>
                    )}
                    {showCountry() && (
                      <div className="w100p">
                        <Form.Group controlId="7491a6ec-8212-4f93-a80b-9cb57deaf477">
                          <Form.Label className="fs14">{translate('EMTF_MD_COUNTRY')}</Form.Label>
                          <Select
                            className="fs14"
                            placeholder={
                              <div className="placeholder-text">{translate('ETMF_LABEL_SELECT_COUNTRY')}</div>
                            }
                            name="filter.data.COUNTRY"
                            value={filterForm?.COUNTRY?.value ? filterForm.COUNTRY.value : null}
                            options={transformToSelectOptions(
                              studyDocumentsFilterOptions?.COUNTRY || [],
                              'fsiId',
                              'fsiName',
                              true
                            )}
                            onChange={(option: any) => {
                              const filterFormData = cloneDeep(filterForm)
                              if (option === null) {
                                filterFormData.COUNTRY = null
                                setFieldValue('filter.data.COUNTRY', null)
                              } else {
                                filterFormData.COUNTRY = {
                                  value: option.value,
                                  fsiId: option.value,
                                  fsiName: option.label,
                                  fsiPropName: option.fsiPropName || option.label
                                }
                                setFieldValue('filter.data.COUNTRY', option.value)
                              }
                              filterFormData.SITE = null
                              setFilterForm(filterFormData)
                              onCountryChange(undefined, true, filterFormData)
                              getZones(undefined, undefined, filterFormData)
                            }}
                            isClearable={true}
                            id="7491a6ec-8212-4f93-a80b-9cb57deaf477"
                            isDisabled={!isCountryEnabled()}
                          />
                          <ErrorMessageStyled name="filter.data.COUNTRY" />
                        </Form.Group>
                      </div>
                    )}
                    {showSite() && (
                      <div className="w100p">
                        <Form.Group controlId="02440be9-b2db-4579-8f3e-3e95973855e4">
                          <Form.Label className="fs14">{translate('EMTF_MD_SITE')}</Form.Label>
                          <Select
                            className="fs14"
                            placeholder={<div className="placeholder-text">{translate('SELECT_SITE')}</div>}
                            name="filter.data.SITE"
                            value={filterForm?.SITE?.value ? filterForm.SITE.value : null}
                            options={transformToSelectOptions(
                              studyDocumentsFilterOptions?.SITE || [],
                              'fsiId',
                              'fsiName',
                              true
                            )}
                            onChange={(option: any) => {
                              const filterFormData = cloneDeep(filterForm)
                              if (option === null) {
                                filterFormData.SITE = null
                                setFieldValue('filter.data.SITE', null)
                              } else {
                                filterFormData.SITE = {
                                  value: option.value,
                                  fsiId: option.value,
                                  fsiName: option.label,
                                  fsiPropName: option.fsiPropName || option.label
                                }
                                setFieldValue('filter.data.SITE', option.value)
                              }
                              setFilterForm(filterFormData)
                              getZones(undefined, undefined, filterFormData)
                            }}
                            isClearable={true}
                            id="02440be9-b2db-4579-8f3e-3e95973855e4"
                            isDisabled={!isSiteEnabled()}
                          />
                          <ErrorMessageStyled name="filter.data.SITE" />
                        </Form.Group>
                      </div>
                    )}
                  </div>

                  {/* Column 3: Additional Filters */}
                  <div className="col-xs-4">
                    {showZone() && (
                      <div className="w100p">
                        <Form.Group controlId="b29c1572-c6c3-43c4-91fb-96f0c228eeaf">
                          <Form.Label className="fs14">{translate('EMTF_ZONE')}</Form.Label>
                          <Select
                            className="fs14"
                            placeholder={<div className="placeholder-text">{translate('SELECT_ZONE')}</div>}
                            name="filter.data.ZONE"
                            value={filterForm?.ZONE?.value ? filterForm.ZONE.value : null}
                            options={transformToSelectOptions(
                              studyDocumentsFilterOptions?.ZONE || [],
                              'fsiId',
                              'fsiName',
                              true
                            )}
                            onChange={(option: any) => {
                              const filterFormData = cloneDeep(filterForm)
                              if (option === null) {
                                filterFormData.ZONE = null
                                setFieldValue('filter.data.ZONE', null)
                              } else {
                                filterFormData.ZONE = {
                                  value: option.value,
                                  fsiId: option.value,
                                  fsiName: option.label,
                                  fsiPropName: option.fsiPropName || option.label
                                }
                                setFieldValue('filter.data.ZONE', option.value)
                              }
                              setFilterForm(filterFormData)
                              getSections(undefined, filterFormData)
                            }}
                            isClearable={true}
                            id="b29c1572-c6c3-43c4-91fb-96f0c228eeaf"
                            isDisabled={!isZoneEnabled()}
                          />
                        </Form.Group>
                      </div>
                    )}
                    {showSection() && (
                      <div className="w100p">
                        <Form.Group controlId="a21cebad-a88c-43c5-89c7-4bdf275e6c9c">
                          <Form.Label className="fs14">{translate('EMTF_SECTION')}</Form.Label>
                          <Select
                            className="fs14"
                            placeholder={<div className="placeholder-text">{translate('SELECT_SECTION')}</div>}
                            name="filter.data.SECTION"
                            value={filterForm?.SECTION?.value ? filterForm.SECTION.value : null}
                            options={transformToSelectOptions(
                              studyDocumentsFilterOptions?.SECTION || [],
                              'fsiId',
                              'fsiName',
                              true
                            )}
                            onChange={(option: any) => {
                              const filterFormData = cloneDeep(filterForm)
                              if (option === null) {
                                filterFormData.SECTION = null
                                setFieldValue('filter.data.SECTION', null)
                              } else {
                                filterFormData.SECTION = {
                                  value: option.value,
                                  fsiId: option.value,
                                  fsiName: option.label,
                                  fsiPropName: option.fsiPropName || option.label
                                }
                                setFieldValue('filter.data.SECTION', option.value)
                              }
                              setFilterForm(filterFormData)
                            }}
                            isClearable={true}
                            id="a21cebad-a88c-43c5-89c7-4bdf275e6c9c"
                            isDisabled={!isSectionEnabled()}
                          />
                        </Form.Group>
                      </div>
                    )}
                    <div className="w100p">
                      <Form.Group controlId="cfb112c4-f197-46a4-8b1d-49b7a65637b2">
                        <Form.Label className="fs14">{translate('TABLE_TH_ARTIFACT_NO')}</Form.Label>
                        <Select
                          className="fs14"
                          placeholder={
                            <div className="placeholder-text">{translate('SELECT_ARTIFACT(S)_PLACEHOLDER')}</div>
                          }
                          name="filter.data.artifacts"
                          value={filterForm?.artifacts?.map((a: any) => a.value) || []}
                          options={
                            properties?.artifact?.values?.map((item: string, index: number) => ({
                              value: index + 1,
                              label: item
                            })) || []
                          }
                          onChange={(option: any) => {
                            const filterFormData = cloneDeep(filterForm)
                            if (option === null || (Array.isArray(option) && option.length === 0)) {
                              filterFormData.artifacts = []
                            } else {
                              filterFormData.artifacts = Array.isArray(option)
                                ? option.map((o: any) => ({ value: o.value, label: o.label }))
                                : [{ value: option.value, label: option.label }]
                            }
                            setFilterForm(filterFormData)
                          }}
                          isClearable={true}
                          id="cfb112c4-f197-46a4-8b1d-49b7a65637b2"
                          isMulti
                        />
                      </Form.Group>
                    </div>
                    <div className="w100p">
                      <Form.Group controlId="8151e9dd-b548-4e98-aa0f-57ca49c45576">
                        <Form.Label className="fs14">{translate('TAG_FILTER')}</Form.Label>
                        <Select
                          className="fs14"
                          placeholder={<div className="placeholder-text">{translate('SELECT_TAG(S)_PLACEHOLDER')}</div>}
                          name="filter.data.tagIds"
                          value={filterForm?.tagIds?.map((t: any) => t.value) || []}
                          options={
                            properties?.tag?.map((item: any) => ({
                              value: item?.id,
                              label: item?.name
                            })) || []
                          }
                          onChange={(option: any) => {
                            const filterFormData = cloneDeep(filterForm)
                            if (option === null || (Array.isArray(option) && option.length === 0)) {
                              filterFormData.tagIds = []
                            } else {
                              filterFormData.tagIds = Array.isArray(option)
                                ? option.map((o: any) => ({ value: o.value, label: o.label }))
                                : [{ value: option.value, label: option.label }]
                            }
                            setFilterForm(filterFormData)
                          }}
                          isClearable={true}
                          id="8151e9dd-b548-4e98-aa0f-57ca49c45576"
                          isMulti
                        />
                      </Form.Group>
                    </div>
                    {!!properties?.milestone?.id && (
                      <div className="w100p">
                        <Form.Group controlId="ffbeb62a-4f5d-4354-9c00-de159e16b67a">
                          <Form.Label className="fs14">{translate('ETMF_OVERVIEW_MILESTONE')}</Form.Label>
                          <Select
                            className="fs14"
                            placeholder={
                              <div className="placeholder-text">{translate('ETMF_LABEL_SELECT_MILESTONE')}</div>
                            }
                            name="filter.data.milestone"
                            value={filterForm?.milestone?.value ?? 0}
                            options={
                              properties?.milestone?.usedValues?.map((item: string, index: number) => ({
                                value: index + 1,
                                label: item
                              })) || []
                            }
                            onChange={(option: any) => {
                              const filterFormData = cloneDeep(filterForm)
                              if (option === null) {
                                filterFormData.milestone = null
                              } else {
                                filterFormData.milestone = { value: option.value, label: option.label }
                              }
                              setFilterForm(filterFormData)
                            }}
                            isClearable={true}
                            id="ffbeb62a-4f5d-4354-9c00-de159e16b67a"
                          />
                        </Form.Group>
                      </div>
                    )}
                    {!!properties?.milestone?.id && (
                      <div className="w100p">
                        <Form.Group controlId="51a2bacc-d22c-4e8a-bb7a-8adb492d3f56">
                          <Form.Label className="fs14">{translate('EMTF_STAGE')}</Form.Label>
                          <Select
                            className="fs14"
                            placeholder={<div className="placeholder-text">{translate('ETMF_LABEL_SELECT_STAGE')}</div>}
                            name="filter.data.stage"
                            value={filterForm?.stage?.value ?? 0}
                            options={
                              properties?.stage?.usedValues?.map((item: string, index: number) => ({
                                value: index + 1,
                                label: item
                              })) || []
                            }
                            onChange={(option: any) => {
                              const filterFormData = cloneDeep(filterForm)
                              if (option === null) {
                                filterFormData.stage = null
                              } else {
                                filterFormData.stage = { value: option.value, label: option.label }
                              }
                              setFilterForm(filterFormData)
                            }}
                            isClearable={true}
                            id="51a2bacc-d22c-4e8a-bb7a-8adb492d3f56"
                          />
                        </Form.Group>
                      </div>
                    )}
                    {!!properties?.milestone?.id && (
                      <div className="w100p">
                        <Form.Group controlId="8a4daa65-a348-4a7e-b928-adf35979d760">
                          <Form.Label className="fs14">{translate('EMTF_RULE')}</Form.Label>
                          <Select
                            className="fs14"
                            placeholder={<div className="placeholder-text">{translate('ETMF_LABEL_SELECT_RULE')}</div>}
                            name="filter.data.rule"
                            value={filterForm?.rule?.value ?? 0}
                            options={
                              properties?.rule?.usedValues?.map((item: string, index: number) => ({
                                value: index + 1,
                                label: item
                              })) || []
                            }
                            onChange={(option: any) => {
                              const filterFormData = cloneDeep(filterForm)
                              if (option === null) {
                                filterFormData.rule = null
                              } else {
                                filterFormData.rule = { value: option.value, label: option.label }
                              }
                              setFilterForm(filterFormData)
                            }}
                            isClearable={true}
                            id="8a4daa65-a348-4a7e-b928-adf35979d760"
                          />
                        </Form.Group>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div>
                  {filterForm?.completionType === 'ALL' ? (
                    <Button
                      data-action={'export'}
                      variant="primary"
                      onClick={() => handleSubmit()}
                      id="8d373f0d-5daa-4a38-b8e0-16863f644f2f"
                    >
                      {translate('BTN_FILTER_EXPORT')}
                    </Button>
                  ) : (
                    <Button variant="primary" onClick={() => handleSubmit()} id="8d373f0d-5daa-4a38-b8e0-16863f644f2f">
                      {translate('BTN_FILTER')}
                    </Button>
                  )}
                  <Button
                    data-action={'reset'}
                    variant="link"
                    onClick={handleReset}
                    id="5e2a36c6-c131-4160-abb5-7b3c36439d3d"
                  >
                    {translate('BTN_CLEAR_FILTER')}
                  </Button>
                </div>
              </>
            )}
          </Formik>
        </div>
      </Collapse>
    </div>
  )
}

export default TableFilter
